import type { UserRole } from '@/types'

export const APP_PERMISSIONS = [
  'dashboard.read',
  'clients.read',
  'clients.manage',
  'caregivers.read',
  'caregivers.manage',
  'schedule.read',
  'schedule.manage',
  'visits.manage',
  'records.read',
  'notes.review',
  'incidents.read',
  'incidents.create',
  'incidents.review',
  'forms.read',
  'forms.manage',
  'forms.send',
  'authorizations.read',
  'authorizations.manage',
  'billing.read',
  'billing.manage',
  'evv.read',
  'evv_exceptions.read',
  'evv_exceptions.review',
  'messages.read',
  'notifications.read',
  'profile.read',
  'time_off.manage',
  'time_off.request',
  'family_feedback.read',
  'family_feedback.manage',
  'family_portal.manage',
  'care_team.manage',
  'care_profile.manage',
  'hospitalizations.manage',
  'documents.read',
  'settings.manage',
  'reports.read',
  'audit.read',
  'admin_tools.manage',
  'open_shifts.read',
  'family_portal.access',
] as const

export type AppPermission = (typeof APP_PERMISSIONS)[number]

type PermissionUserLike = {
  role?: UserRole
  primaryRole?: UserRole
  permissions?: readonly AppPermission[] | null
} | null | undefined

const CAREGIVER_PERMISSIONS: AppPermission[] = [
  'messages.read',
  'notifications.read',
  'profile.read',
  'time_off.request',
  'incidents.create',
  'open_shifts.read',
]

const QA_OFFICER_PERMISSIONS: AppPermission[] = [
  'dashboard.read',
  'clients.read',
  'records.read',
  'incidents.read',
  'forms.read',
  'evv.read',
  'evv_exceptions.read',
  'messages.read',
  'notifications.read',
  'profile.read',
  'documents.read',
]

const BILLING_SPECIALIST_PERMISSIONS: AppPermission[] = [
  'dashboard.read',
  'clients.read',
  'authorizations.read',
  'authorizations.manage',
  'billing.read',
  'billing.manage',
  'evv.read',
  'evv_exceptions.read',
  'messages.read',
  'notifications.read',
  'profile.read',
]

const SUPERVISOR_PERMISSIONS: AppPermission[] = [
  'dashboard.read',
  'clients.read',
  'clients.manage',
  'caregivers.read',
  'caregivers.manage',
  'schedule.read',
  'schedule.manage',
  'visits.manage',
  'records.read',
  'notes.review',
  'incidents.read',
  'incidents.create',
  'incidents.review',
  'forms.read',
  'forms.manage',
  'forms.send',
  'authorizations.read',
  'authorizations.manage',
  'billing.read',
  'billing.manage',
  'evv.read',
  'evv_exceptions.read',
  'evv_exceptions.review',
  'messages.read',
  'notifications.read',
  'profile.read',
  'time_off.manage',
  'family_feedback.read',
  'family_feedback.manage',
  'family_portal.manage',
  'care_team.manage',
  'care_profile.manage',
  'hospitalizations.manage',
  'documents.read',
  'settings.manage',
  'reports.read',
  'audit.read',
  'open_shifts.read',
]

const ADMIN_PERMISSIONS: AppPermission[] = [...APP_PERMISSIONS]

const FAMILY_PERMISSIONS: AppPermission[] = ['family_portal.access']

const DEFAULT_PERMISSIONS_BY_ROLE: Record<UserRole, AppPermission[]> = {
  admin: ADMIN_PERMISSIONS,
  supervisor: SUPERVISOR_PERMISSIONS,
  caregiver: CAREGIVER_PERMISSIONS,
  billing_specialist: BILLING_SPECIALIST_PERMISSIONS,
  qa_officer: QA_OFFICER_PERMISSIONS,
  family: FAMILY_PERMISSIONS,
}

const IMPLIED_PERMISSIONS: Partial<Record<AppPermission, AppPermission[]>> = {
  'clients.manage': ['clients.read'],
  'caregivers.manage': ['caregivers.read'],
  'schedule.manage': ['schedule.read'],
  'visits.manage': ['schedule.read'],
  'forms.manage': ['forms.read'],
  'forms.send': ['forms.read'],
  'authorizations.manage': ['authorizations.read'],
  'billing.manage': ['billing.read'],
  'evv_exceptions.review': ['evv_exceptions.read'],
  'incidents.review': ['incidents.read'],
  'family_feedback.manage': ['family_feedback.read'],
  'settings.manage': ['profile.read', 'notifications.read'],
}

function expandPermissions(permissions: readonly AppPermission[]): AppPermission[] {
  const expanded = new Set<AppPermission>()
  const queue = [...permissions]

  while (queue.length > 0) {
    const permission = queue.shift()
    if (!permission || expanded.has(permission)) continue
    expanded.add(permission)

    for (const impliedPermission of IMPLIED_PERMISSIONS[permission] || []) {
      if (!expanded.has(impliedPermission)) {
        queue.push(impliedPermission)
      }
    }
  }

  return [...expanded]
}

export function getDefaultPermissionsForRole(role: UserRole | undefined | null): AppPermission[] {
  if (!role) return []
  return [...(DEFAULT_PERMISSIONS_BY_ROLE[role] || [])]
}

export function normalizeExplicitPermissions(
  role: UserRole | undefined | null,
  permissions: readonly AppPermission[] | null | undefined = []
): AppPermission[] {
  const defaultPermissions = new Set(getDefaultPermissionsForRole(role))
  return [
    ...new Set(
      (permissions || []).filter(
        permission => APP_PERMISSIONS.includes(permission) && !defaultPermissions.has(permission)
      )
    ),
  ]
}

export function resolvePermissions(
  role: UserRole | undefined | null,
  explicitPermissions?: readonly AppPermission[] | null
): AppPermission[] {
  const resolved = new Set<AppPermission>(getDefaultPermissionsForRole(role))
  for (const permission of explicitPermissions || []) {
    resolved.add(permission)
  }
  return expandPermissions([...resolved])
}

export function getPrimaryRole(user: PermissionUserLike): UserRole | undefined {
  if (!user) return undefined
  return user.primaryRole || user.role
}

export function hasPermission(
  user: PermissionUserLike,
  permission: AppPermission
): boolean {
  if (!user) return false
  const effectivePermissions =
    user.permissions && user.permissions.length > 0
      ? user.permissions
      : resolvePermissions(getPrimaryRole(user))
  return effectivePermissions.includes(permission)
}

export function hasAnyPermission(
  user: PermissionUserLike,
  permissions: readonly AppPermission[]
): boolean {
  return permissions.some(permission => hasPermission(user, permission))
}

export function hasAllPermissions(
  user: PermissionUserLike,
  permissions: readonly AppPermission[]
): boolean {
  return permissions.every(permission => hasPermission(user, permission))
}
